import { provideHttpClient, withXhr } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { ApplicationConfig, inject, Injectable, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_NATIVE_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { es } from 'date-fns/locale/es';
import { appRoutes } from './app.routes';
import { provideAuth } from './core/auth/auth.provider';
import { provideIcons } from './core/icons/icons.provider';
import { provideFuse } from '../@fuse';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomPaginatorIntl } from './core/paginator/custom-pagination-intl.service';

registerLocaleData(localeEs, 'es-ES');

export const langDefinition = {
    'es-ES': { id: 'es-ES', label: 'Español' },
    'en-GB': { id: 'en-GB', label: 'English' },
    'pt-PT': { id: 'pt-PT', label: 'Português' }
};

@Injectable({ providedIn: 'root' })
export class CustomDateAdapter extends NativeDateAdapter {
    constructor(
    ) {
        super();
    }

    getFirstDayOfWeek() {
        return es.options.weekStartsOn;
    }
}


export const appConfig: ApplicationConfig = {
    providers: [
        provideHttpClient(withXhr()),
        provideZoneChangeDetection(),
        provideRouter(appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({scrollPositionRestoration: 'enabled'}),
        ),

        // Material Date Adapter
        {
            provide : DateAdapter,
            useClass: CustomDateAdapter,
        },
        {
            provide : MAT_DATE_FORMATS,
            useValue: MAT_NATIVE_DATE_FORMATS
        },

        {
            provide: LOCALE_ID,
            deps: [TranslocoService],
            useFactory: (translocoService: TranslocoService) => translocoService.getActiveLang()
        },
        { provide: MAT_DATE_LOCALE, useValue: LOCALE_ID },
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntl
        },
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs      : [
                    {
                        id   : 'es-ES',
                        label: 'Español',
                    },
                    {
                        id   : 'en-GB',
                        label: 'English',
                    }
                ],
                defaultLang         : 'es-ES',
                fallbackLang        : 'es-ES',
                reRenderOnLangChange: true,
                prodMode            : true,
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
        const initializerFn = (() =>
            {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            })();
        return initializerFn();
      }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            fuse   : {
                layout : 'classy',
                scheme : 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                    '2xl': '1680px',
                    '3xl': '1920px',
                    '4xl': '2160px'
                },
                theme  : 'theme-default',
                themes : [
                    {
                        id  : 'theme-default',
                        name: 'Default',
                    },
                    {
                        id  : 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id  : 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id  : 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id  : 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id  : 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
